import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { AdminService } from '../../../services/admin.service'
import { AngularRoutes, ContractStatus, EnumNames, Subscription, subscriptionsPath } from '@parkupp/core'
import { collection, collectionData, orderBy, query, where } from '@angular/fire/firestore'
import { GlobalService } from '../../../services/global.service'

interface TableColumn {
    key: string
    label: string
    sortable: boolean
    width?: string
    cssClass?: string
}

@Component({
    selector: 'app-admin-list-rejections-page',
    templateUrl: './admin-list-rejections-page.component.html',
    styleUrls: ['./admin-list-rejections-page.component.scss'],
})
export class AdminListRejectionsPageComponent implements OnInit {
    EnumNames = EnumNames
    ContractStatus = ContractStatus
    subscriptions: Subscription[]
    filteredSubscriptions: Subscription[]

    // Column configuration
    columns: TableColumn[] = [
        { key: 'createdAt', label: 'Created', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'clientRef', label: 'Client', sortable: true },
        { key: 'merchantRef', label: 'Portfolio', sortable: true },
        { key: 'parkingRef', label: 'Parking', sortable: true },
        { key: 'rejectionReason', label: 'Rejection Reason', sortable: true },
    ]

    // Sorting state
    currentSortColumn: string = 'createdAt'
    currentSortDirection: 'asc' | 'desc' = 'desc'
    AngularRoutes = AngularRoutes

    // Status filter
    statusFilter: 'all' | 'merchantRejected' | 'expired' = 'all'

    constructor(public globalService: GlobalService, private adminService: AdminService, private router: Router) {}

    ngOnInit(): void {
        this.runSearch()
    }

    runSearch() {
        const rejectionStatuses: ContractStatus[] = [ContractStatus.merchantRejected, ContractStatus.expired]
        const ref = query(collection(this.adminService.firestore, subscriptionsPath()), orderBy('createdAt', 'desc'), where('status', 'in', rejectionStatuses))
        collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
            this.subscriptions = []
            documents.forEach((documentData) => {
                let subscription = new Subscription(documentData)
                subscription.$key = documentData.documentId

                this.subscriptions.push(subscription)
            })
            this.filteredSubscriptions = this.subscriptions
            this.applySorting()
        })
    }

    searchKeydown($event: any) {
        const input = ($event.target as HTMLInputElement).value.toLowerCase()
        
        // Start with status-filtered list
        let baseList = this.subscriptions
        if (this.statusFilter === 'merchantRejected') {
            baseList = this.subscriptions.filter(s => s.status === ContractStatus.merchantRejected)
        } else if (this.statusFilter === 'expired') {
            baseList = this.subscriptions.filter(s => s.status === ContractStatus.expired)
        }
        
        // Then apply search filter
        this.filteredSubscriptions = baseList.filter((subscription) => {
            return (
                subscription.denormMerchantCompanyName?.toLowerCase().includes(input) ||
                subscription.denormClientFirstName?.toLowerCase().includes(input) ||
                subscription.denormClientLastName?.toLowerCase().includes(input) ||
                subscription.denormParkingTitle?.toLowerCase().includes(input) ||
                subscription.rejectionReason?.toLowerCase().includes(input)
            )
        })
        this.applySorting()
    }

    // Sorting methods
    sortByColumn(columnKey: string): void {
        if (this.currentSortColumn === columnKey) {
            // Toggle direction if same column
            this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            // New column, default to ascending
            this.currentSortColumn = columnKey
            this.currentSortDirection = 'asc'
        }

        this.applySorting()
    }

    applySorting(): void {
        if (!this.filteredSubscriptions) return

        this.filteredSubscriptions.sort((a, b) => {
            let aValue: any
            let bValue: any

            // Get values based on column key
            switch (this.currentSortColumn) {
                case 'createdAt':
                    aValue = a.createdAt?.toDate?.() || a.createdAt
                    bValue = b.createdAt?.toDate?.() || b.createdAt
                    break
                case 'status':
                    aValue = a.statusNiceName || a.status
                    bValue = b.statusNiceName || b.status
                    break
                case 'clientRef':
                    aValue = `${a.denormClientFirstName || ''} ${a.denormClientLastName || ''}`.trim()
                    bValue = `${b.denormClientFirstName || ''} ${b.denormClientLastName || ''}`.trim()
                    break
                case 'merchantRef':
                    aValue = a.denormMerchantCompanyName || ''
                    bValue = b.denormMerchantCompanyName || ''
                    break
                case 'parkingRef':
                    aValue = a.denormParkingTitle || ''
                    bValue = b.denormParkingTitle || ''
                    break
                case 'rejectionReason':
                    aValue = this.getRejectionReasonText(a)
                    bValue = this.getRejectionReasonText(b)
                    break
                default:
                    aValue = a[this.currentSortColumn as keyof Subscription]
                    bValue = b[this.currentSortColumn as keyof Subscription]
            }

            // Handle null/undefined values
            if (aValue === null || aValue === undefined) aValue = ''
            if (bValue === null || bValue === undefined) bValue = ''

            // Compare values
            let comparison = 0
            if (aValue < bValue) comparison = -1
            if (aValue > bValue) comparison = 1

            // Apply sort direction
            return this.currentSortDirection === 'asc' ? comparison : -comparison
        })
    }

    getSortIcon(columnKey: string): string {
        if (!columnKey || this.currentSortColumn !== columnKey) {
            return 'fa fa-sort'
        }
        return this.currentSortDirection === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down'
    }

    isColumnSorted(columnKey: string): boolean {
        return this.currentSortColumn === columnKey
    }

    getFullNameFromSubscription(subscription: Subscription) {
        return `${subscription.denormClientFirstName || ''} ${subscription.denormClientLastName || ''}`.trim()
    }

    getRejectionReasonText(subscription: Subscription): string {
        if (subscription.status === ContractStatus.expired) {
            return 'Automatically timed out after 24 hours'
        }
        return subscription.rejectionReason || 'No reason provided'
    }

    // Status filter method
    setStatusFilter(filter: 'all' | 'merchantRejected' | 'expired'): void {
        this.statusFilter = filter
        this.applyStatusFilter()
    }

    applyStatusFilter(): void {
        if (!this.subscriptions) return

        if (this.statusFilter === 'all') {
            this.filteredSubscriptions = [...this.subscriptions]
        } else if (this.statusFilter === 'merchantRejected') {
            this.filteredSubscriptions = this.subscriptions.filter(s => s.status === ContractStatus.merchantRejected)
        } else if (this.statusFilter === 'expired') {
            this.filteredSubscriptions = this.subscriptions.filter(s => s.status === ContractStatus.expired)
        }
        this.applySorting()
    }

    // Stats computed properties
    getTotalRejections(): number {
        return this.subscriptions?.length || 0
    }

    getMerchantRejectedCount(): number {
        return this.subscriptions?.filter(s => s.status === ContractStatus.merchantRejected).length || 0
    }

    getExpiredCount(): number {
        return this.subscriptions?.filter(s => s.status === ContractStatus.expired).length || 0
    }
}

